import type { ReactNode } from 'react';
import type { TableCellProps } from '@mui/material/TableCell';
import type { SxProps, Theme } from '@mui/material/styles';
import { IconButtonProps } from '@mui/material';
import type { ASC, DESC } from 'framework/list/baseListHelper';

export interface ColumnVisibilityControl {
  columns: { id: string; label: ReactNode }[];
  hiddenColumnIds: string[];
  defaultHiddenColumnIds?: string[];
  onChange: (hiddenColumnIds: string[]) => void;
}

export interface ListTableRow {
  id: string | number;
}

type ListTableSortDirection = typeof ASC | typeof DESC;

export interface ListTableSort {
  columnId: string;
  direction: ListTableSortDirection;
}

export interface ListTableColumn<Row extends ListTableRow> {
  id: string;
  label: ReactNode;
  render: (row: Row) => ReactNode;
  align?: TableCellProps['align'];
  width?: string | number;
  sortable?: boolean;
}

export interface ListTableActionBase {
  id: string;
  label: string;
  icon?: ReactNode;
  tooltip?: string;
  color?: IconButtonProps['color'];
  variant?: 'contained' | 'outlined';
}

export interface ListTableAction<Row extends ListTableRow> extends ListTableActionBase {
  onClick: (row: Row) => void;
  isVisible?: (row: Row) => boolean;
  isDisabled?: (row: Row) => boolean;
}

interface ListTableMainAction extends ListTableActionBase {
  onClick: () => void;
  isDisabled?: boolean;
}

interface ListTableGroupBase<Row extends ListTableRow> {
  id: string | number;
  label: ReactNode;
  rows: Row[];
  count?: number;
  defaultExpanded?: boolean;
  isExpanded?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
}

type ListTableLoadError = { error?: never; retry?: never } | { error: string | undefined; retry: () => void };

export type ListTableGroupLoading = {
  isLoadingMore?: boolean;
  loadMore: () => void;
} & ListTableLoadError;

export type ListTableGroup<Row extends ListTableRow> = ListTableGroupBase<Row> & {
  listTableGroupLoading?: ListTableGroupLoading;
};

interface ListTableBaseProps<Row extends ListTableRow> {
  columns: ListTableColumn<Row>[];
  withChildren?: boolean;
  childrenSource?: keyof Row & string;
  columnVisibilityControl?: ColumnVisibilityControl;
  defaultColumnWidth?: number;
  rowActions?: {
    width?: number;
    maxActions?: number;
    actions?: ListTableAction<Row>[];
  };
  menuActions?: ListTableAction<Row>[];
  mainAction?: ListTableMainAction;
  sort?: ListTableSort | null;
  onSortChange?: (sort: ListTableSort) => void;
  emptyContent?: string;
  isRowHighlighted?: (row: Row) => boolean;
  sx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  virtualHeight?: number;
  rowHeight?: number;
  groupRowHeight?: number;
  overscan?: number;
}

type FlatListTableLoadError =
  { loadMoreError?: never; retryLoadMore?: never } | { loadMoreError: string | undefined; retryLoadMore: () => void };

export type FlatListTableProps<Row extends ListTableRow> = ListTableBaseProps<Row> & {
  rows: Row[];
  groups?: never;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadMore?: () => void;
} & FlatListTableLoadError;

interface GroupedListTableProps<Row extends ListTableRow> extends ListTableBaseProps<Row> {
  groups: ListTableGroup<Row>[];
  rows?: never;
  hasMore?: never;
  isLoadingMore?: never;
  loadMore?: never;
  loadMoreError?: never;
  retryLoadMore?: never;
}

export type ListTableProps<Row extends ListTableRow> = FlatListTableProps<Row> | GroupedListTableProps<Row>;

export enum VirtualTableRowType {
  DATA = 'data',
  CHILDREN = 'children',
  GROUP = 'group',
  TAIL = 'tail',
}

export type VirtualTableRow<Row extends ListTableRow> =
  | { type: VirtualTableRowType.DATA; row: Row; isGrouped: boolean }
  | {
      type: VirtualTableRowType.CHILDREN;
      row: Row;
      isGrouped: boolean;
      depth: number;
      childCount: number;
      isExpanded: boolean;
      key: string;
    }
  | { type: VirtualTableRowType.GROUP; group: ListTableGroup<Row>; isExpanded: boolean }
  | { type: VirtualTableRowType.TAIL; group?: ListTableGroup<Row>; loading: ListTableGroupLoading };

export interface VirtualTableWindow<Row extends ListTableRow> {
  visibleRows: VirtualTableRow<Row>[];
  startIndex: number;
  topSpacerHeight: number;
  bottomSpacerHeight: number;
}
